import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { MultiIdComboboxOption } from '@/components/ui/multi-id-combobox';
import type {
  ChecklistItemKind,
  OrgEventChecklistItemDef,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { ChecklistAddRow } from './ChecklistAddRow';
import { ChecklistItemEditModal } from './ChecklistItemEditModal';
import { ChecklistItemTable } from './ChecklistItemTable';
import {
  RESPONSE_CHECKLIST_LIBRARY,
  blankChecklistItem,
  cloneChecklistLibraryItem,
} from './responseChecklistLibrary';

type Props = {
  items: OrgEventChecklistItemDef[];
  onChange: (next: OrgEventChecklistItemDef[]) => void;
  userOptions: MultiIdComboboxOption[];
  teamOptions: MultiIdComboboxOption[];
  addLabel?: string;
  emptyMessage?: string;
  allowTemplateVariables?: boolean;
  /** Drag to reorder, and place the add button above the table. */
  reorderable?: boolean;
  assignPersonOrRole?: boolean;
  defaultAorIds?: string[];
};

/**
 * The checklist a response creates: a table, a library picker, and the item
 * template modal.
 *
 * Extracted from the automation rule editor once scenarios needed the same
 * thing. A checklist means one thing whether a threshold materializes it or an
 * operator launches it, so it is edited by one component rather than two that
 * drift.
 */
export function ChecklistConfigEditor({
  items,
  onChange,
  userOptions,
  teamOptions,
  addLabel = 'Add Task',
  emptyMessage = 'No response plan configured.',
  allowTemplateVariables = true,
  reorderable = false,
  assignPersonOrRole = false,
  defaultAorIds = [],
}: Props) {
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<{
    mode: 'create' | 'edit';
    item: OrgEventChecklistItemDef;
  } | null>(null);

  const userNameById = useMemo(
    () => new Map(userOptions.map((option) => [option.value, option.label])),
    [userOptions]
  );

  const selectedIds = useMemo(
    () => new Set(items.map((item) => item.id)),
    [items]
  );

  const libraryAvailable = useMemo(
    () =>
      RESPONSE_CHECKLIST_LIBRARY.filter((item) => !selectedIds.has(item.id)),
    [selectedIds]
  );

  const removeItem = (id: string) => {
    onChange(items.filter((item) => item.id !== id));
    if (editor?.item.id === id) setEditor(null);
  };

  const saveEditor = (next: OrgEventChecklistItemDef) => {
    if (editor?.mode === 'create') {
      onChange([...items, next]);
      return;
    }
    onChange(
      items.map((item) => (item.id === next.id ? { ...item, ...next } : item))
    );
  };

  const startNewItem = (kind: ChecklistItemKind) => {
    setIsAdding(false);
    setEditor({ mode: 'create', item: blankChecklistItem(kind) });
  };

  const addButton = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="tw:w-fit tw:gap-1"
      disabled={isAdding}
      onClick={() => setIsAdding(true)}
    >
      <Plus className="tw:h-3.5 tw:w-3.5" />
      {addLabel}
    </Button>
  );

  return (
    <>
      <ChecklistItemTable
        items={items}
        userNameById={userNameById}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search checklist items…"
        emptyMessage={emptyMessage}
        onSelect={(item) => setEditor({ mode: 'edit', item })}
        renderAction={(item) => (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="tw:h-7 tw:w-7 tw:p-0"
            aria-label={`Remove ${item.label}`}
            onClick={() => removeItem(item.id)}
          >
            <Trash2 className="tw:h-3.5 tw:w-3.5" />
          </Button>
        )}
        renderLeadingRow={
          isAdding
            ? () => (
                <ChecklistAddRow
                  library={libraryAvailable}
                  userNameById={userNameById}
                  onPickLibraryItem={(item) => {
                    if (!selectedIds.has(item.id)) {
                      onChange([...items, cloneChecklistLibraryItem(item)]);
                    }
                    setIsAdding(false);
                  }}
                  onCreateNew={startNewItem}
                  onCancel={() => setIsAdding(false)}
                />
              )
            : undefined
        }
        reorderable={reorderable}
        onReorder={onChange}
        toolbar={reorderable ? addButton : undefined}
      />

      {reorderable ? null : addButton}

      <ChecklistItemEditModal
        item={editor?.item ?? null}
        isNew={editor?.mode === 'create'}
        userOptions={userOptions}
        teamOptions={teamOptions}
        allowTemplateVariables={allowTemplateVariables}
        siblingItems={items}
        assignPersonOrRole={assignPersonOrRole}
        defaultAorIds={defaultAorIds}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
        onSave={saveEditor}
      />
    </>
  );
}
